import { useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useFreelancerInvoices } from '../../hooks/useFreelancerInvoices';
import { StatusBadge } from '../../components/workflow/StatusBadge';
import { Spinner } from '../../components/common/Spinner';
import { formatMonthYear } from '../../utils/dateFormat';
import { LEGACY_CZ_NUMBER_LOCALE, formatNumber } from '../../utils/localeFormat';

function formatCurrency(value: number): string {
  return formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE);
}

export default function FreelancerInvoiceReportPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const now = new Date();

  const year = searchParams.get('year') ? parseInt(searchParams.get('year')!, 10) : now.getFullYear();
  const month = searchParams.get('month') ? parseInt(searchParams.get('month')!, 10) : now.getMonth() + 1;
  const filterUserId = searchParams.get('userId') ? parseInt(searchParams.get('userId')!, 10) : '';

  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null) next.delete(k);
        else next.set(k, v);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const { data: invoices, isLoading } = useFreelancerInvoices(year, month);

  const filtered = invoices?.filter((inv) => !filterUserId || inv.userId === filterUserId);

  // MKS-351 — "October 2026" from the shared util.
  const monthLabel = formatMonthYear(new Date(year, month - 1));

  const goToPrev = () => {
    if (month === 1) updateParams({ year: String(year - 1), month: '12' });
    else updateParams({ month: String(month - 1) });
  };

  const goToNext = () => {
    if (month === 12) updateParams({ year: String(year + 1), month: '1' });
    else updateParams({ month: String(month + 1) });
  };

  const grandTotal = filtered?.reduce((sum, inv) => sum + inv.netAmount, 0) ?? 0;
  const salaryTotal = filtered?.reduce((sum, inv) => sum + (inv.salaryAmount ?? 0), 0) ?? 0;
  const invoiceAfterSalaryTotal = filtered?.reduce((sum, inv) => sum + inv.invoiceAfterSalary, 0) ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <button onClick={() => navigate('/reports')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
          &larr; Back to Reports
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Freelancer Invoices</h1>
        <p className="text-sm text-gray-500 mt-1">
          Cross-check expected freelancer invoices against received invoices.
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-3">
          <button onClick={goToPrev} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50">
            &larr;
          </button>
          <span className="text-sm font-medium text-gray-900 min-w-[160px] text-center">{monthLabel}</span>
          <button onClick={goToNext} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50">
            &rarr;
          </button>
        </div>
        {invoices && invoices.length > 0 && (
          <select
            value={filterUserId}
            onChange={(e) => updateParams({ userId: e.target.value || null })}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700"
          >
            <option value="">All freelancers</option>
            {invoices.map((inv) => (
              <option key={inv.userId} value={inv.userId}>{inv.userName}</option>
            ))}
          </select>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Spinner />
        </div>
      ) : !filtered || filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
          No freelancer invoices for {monthLabel}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Freelancer</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Billing</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Expenses</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Salary</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered?.map((inv) => (
                <tr key={inv.userId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{inv.userName}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{inv.totalHours.toFixed(1)}h</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(inv.totalBilling)}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{inv.totalExpenses > 0 ? formatCurrency(inv.totalExpenses) : '-'}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="text-sm font-medium text-gray-900">{formatCurrency(inv.netAmount)}</div>
                    {inv.totalExtraCosts > 0 && (
                      <div className="text-xs text-red-500">-{formatCurrency(inv.totalExtraCosts)} deducted</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {inv.salaryAmount != null && inv.salaryAmount > 0 ? (
                      <span className="text-red-600">-{formatCurrency(inv.salaryAmount)}</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(inv.invoiceAfterSalary)}</td>
                  <td className="px-4 py-3">
                    <StatusBadge state={inv.state} size="sm" />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/reports/freelancer-invoices/${inv.userId}/${year}/${month}`}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-medium">
                <td className="px-4 py-3 text-sm text-gray-900">Total ({filtered?.length ?? 0} freelancer{(filtered?.length ?? 0) !== 1 ? 's' : ''})</td>
                <td className="px-4 py-3 text-sm text-gray-900 text-right">{(filtered?.reduce((s, i) => s + i.totalHours, 0) ?? 0).toFixed(1)}h</td>
                <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(filtered?.reduce((s, i) => s + i.totalBilling, 0) ?? 0)}</td>
                <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(filtered?.reduce((s, i) => s + i.totalExpenses, 0) ?? 0)}</td>
                <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">{formatCurrency(grandTotal)}</td>
                <td className="px-4 py-3 text-sm text-red-600 text-right font-semibold">{salaryTotal > 0 ? `-${formatCurrency(salaryTotal)}` : '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">{formatCurrency(invoiceAfterSalaryTotal)}</td>
                <td colSpan={2} />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
